import React from 'react';
import { CustomModal } from '../../../../common/CustomModal';
import {
  AlertCircle,
  Building2,
  Check,
  CheckCircle,
  CreditCard,
  FileText,
  Info,
  Loader2,
  Plus,
  ScanLine,
  Trash2,
  Upload,
  Wallet,
  X,
} from 'lucide-react';
import '../../../../../styles/components/auction-client-pay.scss';
import { toast } from 'react-toastify';
import { auctionPayment, getUserDetails } from '../services/mzadcomPaymentService';
import i18n from '../../../../../i18n';
import { convertPdfToImage } from '../../../../../utils/pdfToImage';
import { scanReceipt } from '../../../../home/pages/Enroll/services/enrollService';

export class MzadcomPayModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      paymentMethod: '',
      walletBalance: 0,
      editableBalances: {},
      loadingProfile: true,

      // Offline payment fields
      entryScanStates: {},
      fieldErrors: {},
      bankNameError: '',
      bankName: props.user?.bank || '',
      paymentEntries: [
        {
          id: Date.now(),
          amount: '',
          receipt: '',
          receiptImage: null,
          previewUrl: null,
        },
      ],
      maxPaymentEntries: 10,
      // Online payment fields
      cardType: '',

      showPolicies: {
        offline: false,
        online: false,
      },
    };
  }

  async componentDidMount() {
    this.initializeBalances();
    await this.fetchUserDetails();
  }
  // Optional: Add cleanup on component unmount
  componentWillUnmount() {
    // Clean up any remaining preview URLs
    this.state.paymentEntries.forEach((entry) => {
      if (entry.previewUrl) {
        URL.revokeObjectURL(entry.previewUrl);
      }
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedAuctions !== this.props.selectedAuctions && this.props.selectedAuctions) {
      this.initializeBalances();
    }
    if (!prevProps.show && this.props.show) {
      this.fetchUserDetails();
      const updatedBalances = {};
      this.props.selectedAuctions.forEach((auction) => {
        updatedBalances[auction.id] = parseFloat(auction.balance_amount || 0);
      });
      this.setState({ editableBalances: updatedBalances });
    }
  }

  initializeBalances = () => {
    if (this.props.selectedAuctions) {
      const editableBalances = {};
      this.props.selectedAuctions.forEach((auction) => {
        // Map the correct property names from the API response
        editableBalances[auction.id] = parseFloat(auction.balance_amount || 0);
      });
      this.setState({ editableBalances });
    }
  };

  fetchUserDetails = async () => {
    this.setState({ loadingProfile: true });
    try {
      const response = await getUserDetails();

      if (response.success) {
        const { data } = response;
        this.setState({
          walletBalance: parseFloat(data.wallet_amount || 0),
        });
      }
    } catch (error) {
      console.error('Failed to fetch wallet balance', error);
    } finally {
      this.setState({ loadingProfile: false });
    }
  };

  handleReceiptScan = async (entryId) => {
    const entry = this.state.paymentEntries.find((e) => e.id === entryId);
    if (!entry?.receiptImage) return;

    this.setState((prev) => ({
      entryScanStates: {
        ...prev.entryScanStates,
        [entryId]: { isScanning: true, scanSuccess: false, scanError: null },
      },
    }));

    try {
      let uploadFile = entry.receiptImage;

      // Convert PDF → PNG (same as RechargeForm)
      if (entry.receiptImage.type === 'application/pdf') {
        uploadFile = await convertPdfToImage(entry.receiptImage);
      }

      const formData = new FormData();
      formData.append('receipt', uploadFile);

      const result = await scanReceipt(formData);

      if (result.success && result.data) {
        const { bank_name, receipt_number, amount, transfer_date } = result.data.form; // ADD bank_name

        this.setState((prev) => ({
          paymentEntries: prev.paymentEntries.map((e) =>
            e.id === entryId
              ? {
                  ...e,
                  receipt: receipt_number || e.receipt,
                  amount: amount || e.amount,
                  transferDate: transfer_date || e.transferDate,
                }
              : e
          ),
          // ADD: fill bankName from scan (same as RechargeForm fills formInput.bank)
          bankName: bank_name || prev.bankName,
          // ADD: clear bankName error if it was filled
          bankNameError: bank_name ? '' : prev.bankNameError,
          entryScanStates: {
            ...prev.entryScanStates,
            [entryId]: { isScanning: false, scanSuccess: true, scanError: null },
          },
          fieldErrors: {
            ...prev.fieldErrors,
            [entryId]: {
              ...(prev.fieldErrors[entryId] || {}),
              receipt: undefined,
              amount: undefined,
              transferDate: undefined,
            },
          },
        }));

        toast.success('Receipt scanned successfully! Please verify the fields.');
      } else {
        this.setState((prev) => ({
          entryScanStates: {
            ...prev.entryScanStates,
            [entryId]: {
              isScanning: false,
              scanSuccess: false,
              scanError: 'Could not extract data. Please fill manually.',
            },
          },
        }));
      }
    } catch (err) {
      console.error('[ReceiptScan]', err);
      this.setState((prev) => ({
        entryScanStates: {
          ...prev.entryScanStates,
          [entryId]: {
            isScanning: false,
            scanSuccess: false,
            scanError: err.message || 'Scan failed. Please fill the form manually.',
          },
        },
      }));
    }
  };

  handleBalanceChange = (auctionId, value) => {
    const auction = this.props.selectedAuctions.find((a) => a.id === auctionId);
    const maxBalance = parseFloat(auction.balance_amount || 0);
    const numValue = parseFloat(value) || 0;

    let newBalance = numValue;
    if (numValue > maxBalance) {
      newBalance = maxBalance;
      toast.warning(`Maximum payment amount for ${auction.title} is ${maxBalance.toFixed(2)}`);
    } else if (numValue < 0) {
      newBalance = 0;
      toast.error('Payment amount cannot be negative.');
    }

    this.setState((prevState) => ({
      editableBalances: {
        ...prevState.editableBalances,
        [auctionId]: newBalance,
      },
    }));
  };

  getTotalPaymentAmount = () => {
    return Object.values(this.state.editableBalances).reduce((total, amount) => total + amount, 0);
  };

  getCommission = () => {
    const { cardType } = this.state;
    const amount = this.getTotalPaymentAmount();

    if (cardType === 'debit') {
      return amount * 0.015;
    } else if (cardType === 'credit') {
      return amount * 0.02;
    }
    return 0;
  };

  getSubtotal = () => {
    const amount = this.getTotalPaymentAmount();
    const commission = this.getCommission();
    return amount + commission;
  };
  getPendingBalance = () => {
    const totalBalance = (this.props.selectedAuctions || []).reduce(
      (sum, auction) => sum + parseFloat(auction.balance_amount || 0),
      0
    );
    return totalBalance - this.getSubtotal();
  };
  canPayWithWallet = () => {
    const amount = this.getTotalPaymentAmount();
    return amount <= this.state.walletBalance;
  };

  canPayOnline = () => {
    const amount = this.getTotalPaymentAmount();
    return amount < 5000;
  };

  togglePolicy = (type) => {
    this.setState((prevState) => ({
      showPolicies: {
        ...prevState.showPolicies,
        [type]: !prevState.showPolicies[type],
      },
    }));
  };

  // Add payment entry
  addPaymentEntry = () => {
    const { paymentEntries, maxPaymentEntries } = this.state;

    // Check maximum limit
    if (paymentEntries.length >= maxPaymentEntries) {
      toast.warning(`Maximum ${maxPaymentEntries} payment entries allowed.`);
      return;
    }

    // Optional: Check if previous entries are filled before adding new
    const lastEntry = paymentEntries[paymentEntries.length - 1];
    if (!lastEntry.amount || !lastEntry.receipt) {
      toast.info('Please complete the current entry before adding a new one.');
      return;
    }

    this.setState((prevState) => ({
      paymentEntries: [
        ...prevState.paymentEntries,
        {
          id: Date.now(),
          amount: '',
          receipt: '',
          transferDate: '',
          receiptImage: null,
          previewUrl: null,
        },
      ],
    }));
  };

  //  RemovePaymentEntry - ensure at least one entry remains
  removePaymentEntry = (id) => {
    const { paymentEntries } = this.state;

    if (paymentEntries.length <= 1) {
      toast.info('At least one payment entry is required.');
      return;
    }

    // Find and revoke URL for the entry being removed
    const entryToRemove = paymentEntries.find((entry) => entry.id === id);
    if (entryToRemove && entryToRemove.previewUrl) {
      URL.revokeObjectURL(entryToRemove.previewUrl);
    }

    this.setState((prevState) => ({
      paymentEntries: prevState.paymentEntries.filter((entry) => entry.id !== id),
    }));
  };

  // Update payment entry field
  updatePaymentEntry = (id, field, value) => {
    this.setState((prevState) => ({
      paymentEntries: prevState.paymentEntries.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      ),
      fieldErrors: {
        ...prevState.fieldErrors,
        [id]: { ...(prevState.fieldErrors[id] || {}), [field]: undefined },
      },
    }));
  };

  // Handle file upload
  isDuplicateFile = (currentId, file) => {
    if (!file) return false;

    const { paymentEntries } = this.state;

    return paymentEntries.some((entry) => {
      if (entry.id === currentId || !entry.receiptImage) {
        return false;
      }

      const existingFile = entry.receiptImage;

      // Compare: name, size, lastModified, and type
      return (
        existingFile.name === file.name &&
        existingFile.size === file.size &&
        existingFile.lastModified === file.lastModified &&
        existingFile.type === file.type
      );
    });
  };
  // Validate total file size (e.g., max 50MB total)
  handleFileUpload = (id, e) => {
    const file = e.target.files[0];
    if (file) {
      // Check individual file size (e.g., max 10MB per file)
      const maxFileSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxFileSize) {
        toast.error('File size must be less than 10MB.');
        e.target.value = ''; // Reset input
        return;
      }
      // Duplicate check
      if (this.isDuplicateFile(id, file)) {
        toast.error('This file is already uploaded in another entry. Please use a different file.');
        e.target.value = '';
        return;
      }
      // Check total files size
      const totalSize =
        this.state.paymentEntries.reduce((sum, entry) => sum + (entry.receiptImage?.size || 0), 0) +
        file.size;

      const maxTotalSize = 50 * 1024 * 1024; // 50MB total
      if (totalSize > maxTotalSize) {
        toast.error('Total file size must be less than 50MB.');
        e.target.value = ''; // Reset input
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      this.setState((prevState) => ({
        paymentEntries: prevState.paymentEntries.map((entry) =>
          entry.id === id ? { ...entry, receiptImage: file, previewUrl } : entry
        ),
        fieldErrors: {
          ...prevState.fieldErrors,
          [id]: { ...(prevState.fieldErrors[id] || {}), receiptImage: undefined },
        },
      }));
    }
  };

  // Remove file
  removeFile = (id) => {
    this.setState((prevState) => ({
      paymentEntries: prevState.paymentEntries.map((entry) => {
        if (entry.id === id && entry.previewUrl) {
          URL.revokeObjectURL(entry.previewUrl);
          return { ...entry, receiptImage: null, previewUrl: null };
        }
        return entry;
      }),
      entryScanStates: {
        ...prevState.entryScanStates,
        [id]: { isScanning: false, scanSuccess: false, scanError: null },
      },
    }));
  };

  handlePayment = async () => {
    const { paymentMethod, editableBalances, bankName, paymentEntries, cardType } = this.state;
    const { selectedAuctions } = this.props;
    const totalAmount = this.getTotalPaymentAmount();
    if (totalAmount <= 0) {
      toast.error('Payment amount must be greater than 0.');
      return;
    }
    // ── ADD: collect inline field errors (runs before existing toast logic) ──
    if (paymentMethod === 'offline') {
      const newFieldErrors = {};
      let hasInlineErrors = false;

      if (!bankName || bankName.trim() === '') {
        this.setState({ bankNameError: i18n.t('field_required') });
        hasInlineErrors = true;
      } else {
        this.setState({ bankNameError: '' });
      }

      paymentEntries.forEach((entry) => {
        const entryErrors = {};

        if (!entry.transferDate || entry.transferDate.trim() === '')
          entryErrors.transferDate = i18n.t('field_required');

        if (!entry.amount || parseFloat(entry.amount) <= 0)
          entryErrors.amount = i18n.t('field_required');

        if (!entry.receipt || entry.receipt.trim() === '')
          entryErrors.receipt = i18n.t('field_required');
        else if (!/^[A-Za-z0-9!@#$%^&*()_\-+=[\]{}:;"'<>?,./]{4,}$/.test(entry.receipt))
          entryErrors.receipt = i18n.t('receipt_min_4_chars');

        if (!entry.receiptImage) entryErrors.receiptImage = i18n.t('field_required');

        if (Object.keys(entryErrors).length) {
          newFieldErrors[entry.id] = entryErrors;
          hasInlineErrors = true;
        }
      });

      this.setState({ fieldErrors: newFieldErrors });

      if (hasInlineErrors) return; // stops here, existing toast block never runs if there are inline errors
    }
    // Validate offline fields
    if (paymentMethod === 'offline') {
      // Validate bank name
      if (!bankName || bankName.trim() === '') {
        toast.error('Bank name is required.');
        return;
      }

      // Check for duplicate receipt numbers first
      const receiptNumbers = paymentEntries
        .map((entry) => entry.receipt.trim().toUpperCase())
        .filter((receipt) => receipt !== '');

      const duplicateReceipts = receiptNumbers.filter(
        (receipt, index) => receiptNumbers.indexOf(receipt) !== index
      );

      if (duplicateReceipts.length > 0) {
        toast.error('Receipt numbers must be unique. Duplicate receipt number(s) found.');
        return;
      }

      // Validate all payment entries
      for (let i = 0; i < paymentEntries.length; i++) {
        const entry = paymentEntries[i];

        if (!entry.amount || parseFloat(entry.amount) <= 0) {
          toast.error(`Entry ${i + 1}: Amount is required and must be greater than 0.`);
          return;
        }

        if (!entry.receipt || entry.receipt.trim() === '') {
          toast.error(`Entry ${i + 1}: Receipt number is required.`);
          return;
        }

        if (!/^[A-Za-z0-9!@#$%^&*()_\-+=[\]{}:;"'<>?,./]{4,}$/.test(entry.receipt)) {
          toast.error(`Entry ${i + 1}: Receipt number must be at least 4 characters.`);
          return;
        }

        if (!entry.receiptImage) {
          toast.error(`Entry ${i + 1}: Receipt image is required.`);
          return;
        }
      }

      // Validate total payment entries amount matches selected amount
      const totalEntriesAmount = paymentEntries.reduce(
        (sum, entry) => sum + parseFloat(entry.amount || 0),
        0
      );

      if (Math.abs(totalEntriesAmount - totalAmount) > 0.01) {
        toast.warning(
          `Total entries amount (${totalEntriesAmount.toFixed(
            2
          )}) should match payment amount (${totalAmount.toFixed(2)}).`
        );
        return;
      }
    }

    // Validate online payment
    if (paymentMethod === 'online') {
      if (!cardType) {
        toast.error('Please select a card type.');
        return;
      }

      if (!this.canPayOnline()) {
        toast.error('Online payment is only available for amounts less than 5000.');
        return;
      }
    }

    // Validate wallet payment
    if (paymentMethod === 'wallet') {
      if (!this.canPayWithWallet()) {
        toast.error('Insufficient wallet balance.');
        return;
      }
    }

    // Create FormData
    const formData = new FormData();
    let subtotal = 0;

    // Add auction data
    selectedAuctions.forEach((auction, index) => {
      const amount = editableBalances[auction.id] || 0;
      subtotal += amount;

      formData.append(`auctions[${index}][id]`, auction.id);
      formData.append(`auctions[${index}][amount]`, amount);
    });

    formData.append('subtotal', subtotal);
    formData.append('paymentMethod', paymentMethod);

    // Add offline payment data
    if (paymentMethod === 'offline') {
      formData.append('bankName', bankName);

      const paymentsData = paymentEntries.map((entry) => ({
        amount: parseFloat(entry.amount).toFixed(2),
        receiptNumber: entry.receipt.trim(),
        transferDate: entry.transferDate || '',
        receiptImage: entry.receiptImage,
      }));

      // Append payments in standard nested array format
      paymentsData.forEach((payment, index) => {
        formData.append(`payments[${index}][amount]`, payment.amount);
        formData.append(`payments[${index}][receiptNumber]`, payment.receiptNumber);
        formData.append(`payments[${index}][transferDate]`, payment.transferDate);
        formData.append(`payments[${index}][receiptImage]`, payment.receiptImage);
      });

      // Add count of payment entries
      formData.append('paymentsCount', paymentEntries.length);
    }

    try {
      this.setState({ submitting: true });
      const response = await auctionPayment(formData);
      if (response.success) {
        toast.success(response.message || 'Payment successful');
        this.props.onPaymentComplete?.();
        this.handleCancel();
      } else {
        toast.error(response.message || 'Payment failed');
      }
    } catch (error) {
      console.error(error);

      const res = error?.response?.data || error;

      // backend validation object
      const backendErrors = res?.data || res?.errors || {};

      // map Laravel-style errors -> UI structure
      const newFieldErrors = {};
      let globalMessage = res?.message || 'Something went wrong';

      Object.keys(backendErrors).forEach((key) => {
        const messages = backendErrors[key];
        const message = Array.isArray(messages) ? messages[0] : messages;

        // Example key: payments.0.receiptNumber
        const match = key.match(/^payments\.(\d+)\.(.+)$/);

        if (match) {
          const index = parseInt(match[1], 10);
          const field = match[2];

          const entryId = this.state.paymentEntries?.[index]?.id;

          if (entryId) {
            if (!newFieldErrors[entryId]) newFieldErrors[entryId] = {};

            // map backend field → frontend field name
            if (field === 'receiptNumber') {
              newFieldErrors[entryId].receipt = message;
            } else if (field === 'receiptImage') {
              newFieldErrors[entryId].receiptImage = message;
            } else if (field === 'amount') {
              newFieldErrors[entryId].amount = message;
            } else if (field === 'transferDate') {
              newFieldErrors[entryId].transferDate = message;
            }
          }
        } else {
          // global errors (bank, subtotal, etc.)
          globalMessage = message;
        }
      });

      // apply UI errors
      if (Object.keys(newFieldErrors).length > 0) {
        this.setState({ fieldErrors: newFieldErrors });

        //  show toast even when field errors exist
        const firstErrorKey = Object.keys(backendErrors)[0];
        const messages = backendErrors[firstErrorKey];
        const firstMessage = Array.isArray(messages) ? messages[0] : messages;

        toast.error(firstMessage || globalMessage);
      } else {
        toast.error(globalMessage);
      }
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleCancel = () => {
    // Clean up preview URLs to prevent memory leaks
    this.state.paymentEntries.forEach((entry) => {
      if (entry.previewUrl) {
        URL.revokeObjectURL(entry.previewUrl);
      }
    });
    // Reset form state
    this.setState({
      walletBalance: 0,
      editableBalances: {},
      paymentMethod: '',
      bankName: this.props.user?.bank || '',
      paymentEntries: [
        {
          id: Date.now(),
          amount: '',
          receipt: '',
          transferDate: '',
          receiptImage: null,
          previewUrl: null,
        },
      ],
      cardType: '',
      showPolicies: { offline: false, online: false },
      submitting: false,
      fieldErrors: {},
      bankNameError: '',
      entryScanStates: {},
    });

    this.props.onClose();
  };

  renderEntryError = (entryId, field) => {
    const msg = this.state.fieldErrors?.[entryId]?.[field];
    if (!msg) return null;
    return (
      <span className="mze-field__error">
        <AlertCircle size={12} />
        {msg}
      </span>
    );
  };

  render() {
    const { show, selectedAuctions = [] } = this.props;
    const {
      paymentMethod,
      walletBalance,
      editableBalances,
      bankName,
      paymentEntries,
      cardType,
      showPolicies,
      loadingProfile,
    } = this.state;

    const totalPaymentAmount = this.getTotalPaymentAmount();
    const today = new Date().toISOString().split('T')[0];

    return (
      <CustomModal
        isOpen={show}
        header={i18n.t('payment_for_selected_auction')}
        onClose={this.handleCancel}
      >
        <div className="payment-container">
          {loadingProfile ? (
            <div className="skeleton-loader">
              <div className="skeleton skeleton-header"></div>
              <div className="skeleton skeleton-item"></div>
              <div className="skeleton skeleton-item"></div>
              <div className="skeleton skeleton-item"></div>
              <div className="skeleton skeleton-item"></div>
              <div className="skeleton skeleton-item"></div>
            </div>
          ) : (
            <div className="payment-content">
              <div className="auction-summary">
                <h3>{i18n.t('payment_summary')}</h3>
                {selectedAuctions.map((auction) => (
                  <div key={auction.id} className="auction-item">
                    <div className="auction-info">
                      <div className="auction-id">MZAD {auction.id}</div>
                      <div className="auction-name">{auction.title}</div>
                      <div className="amounts">
                        <span>
                          {i18n.t('total')} : {parseFloat(auction.total_amount || 0).toFixed(2)}
                        </span>
                        <span>
                          {i18n.t('paid')} : {parseFloat(auction.total_paid || 0).toFixed(2)}
                        </span>
                        <span>
                          {i18n.t('balance')} : {parseFloat(auction.balance_amount || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="payment-amount-edit">
                      <label>{i18n.t('payment_amount')}</label>
                      <div
                        className={`amount-input-group {
                        editableBalances[auction.id] > parseFloat(auction.balance_amount || 0)
                          ? 'error'
                          : editableBalances[auction.id] > 0
                          ? 'valid'
                          : ''
                      }`}
                      >
                        <input
                          type="number"
                          value={editableBalances[auction.id] || ''}
                          onChange={(e) => this.handleBalanceChange(auction.id, e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          onBlur={(e) => {
                            // Remove leading zeros on blur
                            const val = parseFloat(e.target.value) || 0;
                            this.handleBalanceChange(auction.id, val.toString());
                          }}
                          max={parseFloat(auction.balance_amount || 0)}
                          min="0"
                          step="0.01"
                          placeholder={`Max: ${parseFloat(auction.balance_amount || 0).toFixed(2)}`}
                        />
                      </div>
                      {editableBalances[auction.id] > parseFloat(auction.balance_amount || 0) && (
                        <div className="error-message">
                          <AlertCircle className="error-icon" />
                          {i18n.t('exceeds_limit_of')}
                          {parseFloat(auction.balance_amount || 0).toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div className="summary-row total">
                  <span>{i18n.t('total_payment_amount')} </span>
                  <span>{totalPaymentAmount.toFixed(2)}</span>
                </div>
              </div>

              <div className="payment-methods">
                <h3>{i18n.t('select_payment_method')}</h3>

                {/* Offline Payment */}
                <div className={`payment-option {paymentMethod === 'offline' ? 'selected' : ''}`}>
                  <div
                    className="option-header"
                    onClick={() => this.setState({ paymentMethod: 'offline' })}
                  >
                    <Building2 className="option-icon" />
                    <span>{i18n.t('offline_payment')}</span>
                    <button
                      className="policy-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.togglePolicy('offline');
                      }}
                    >
                      <Info className="icon" />
                    </button>
                  </div>

                  {showPolicies.offline && (
                    <div className="policy-section">
                      <h4>{i18n.t('wallet_amount_deposit_and_refund_policy')}</h4>
                      <ul>
                        <li>{i18n.t('deposits_processed_within')}</li>
                        <li>{i18n.t('refunds_take')}</li>
                        <li>{i18n.t('minimum_deposit_amount')}</li>
                        <li>{i18n.t('transaction_fees_may_apply')}</li>
                      </ul>
                    </div>
                  )}

                  {paymentMethod === 'offline' && (
                    <div className="payment-form">
                      <div className="form-group">
                        <label>{i18n.t('bank_name')} *</label>
                        <input
                          type="text"
                          value={bankName}
                          onChange={(e) => this.setState({ bankName: e.target.value })}
                          className={`form-input${this.state.bankNameError ? ' input-error' : ''}`}
                          required
                        />
                        {this.state.bankNameError && (
                          <span className="mze-field__error">
                            <AlertCircle size={12} /> {this.state.bankNameError}
                          </span>
                        )}
                      </div>

                      <div className="payments-section">
                        <div className="section-header">
                          <label>
                            {i18n.t('receipt_details')}
                            <span className="entries-count">
                              ({paymentEntries.length}/{this.state.maxPaymentEntries})
                            </span>
                          </label>
                          <button type="button" onClick={this.addPaymentEntry} className="btn-add">
                            <Plus className="icon" />
                            {i18n.t('add_receipt')}
                          </button>
                        </div>

                        {paymentEntries.map((entry, index) => (
                          <div key={entry.id} className="payment-entry">
                            <div className="entry-header">
                              <span className="entry-number">
                                {i18n.t('receipt')} {index + 1}
                              </span>
                              {paymentEntries.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => this.removePaymentEntry(entry.id)}
                                  className="btn-delete"
                                >
                                  <Trash2 className="icon" />
                                </button>
                              )}
                            </div>
                            <div className="form-group">
                              <label className="mze-field__label">
                                {i18n.t('receipt_image')}
                                <span className="mze-field__required">*</span>
                              </label>

                              <input
                                type="file"
                                id={`receipt-upload-${entry.id}`}
                                accept="image/*,application/pdf"
                                style={{ display: 'none' }}
                                onChange={(e) => this.handleFileUpload(entry.id, e)}
                              />

                              {!entry.receiptImage ? (
                                <div
                                  className={`mze-upload-zone ${this.state.draggingId === entry.id ? 'mze-upload-zone--dragover' : ''}`}
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    this.setState({ draggingId: entry.id });
                                  }}
                                  onDragLeave={() => this.setState({ draggingId: null })}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    this.setState({ draggingId: null });
                                    // reuse existing handleFileUpload by creating a synthetic event
                                    const syntheticE = {
                                      target: { files: e.dataTransfer.files, value: '' },
                                    };
                                    this.handleFileUpload(entry.id, syntheticE);
                                  }}
                                >
                                  <Upload size={24} className="mze-upload-zone__icon" />
                                  <p className="mze-upload-zone__title">
                                    {i18n.t('drop_receipt_here')}
                                  </p>
                                  <p className="mze-upload-zone__sub">
                                    JPG, PNG or PDF — max 10 MB
                                  </p>
                                  <button
                                    type="button"
                                    className="mze-upload-zone__btn"
                                    onClick={() =>
                                      document.getElementById(`receipt-upload-${entry.id}`).click()
                                    }
                                  >
                                    {i18n.t('browse_file')}
                                  </button>
                                </div>
                              ) : (
                                <div className="mze-receipt-preview">
                                  <div className="mze-receipt-preview__header">
                                    <FileText
                                      size={15}
                                      className="mze-receipt-preview__file-icon"
                                    />
                                    <span className="mze-receipt-preview__name">
                                      {entry.receiptImage.name}
                                    </span>
                                    <span className="mze-receipt-preview__size">
                                      {entry.receiptImage.size < 1048576
                                        ? Math.round(entry.receiptImage.size / 1024) + ' KB'
                                        : (entry.receiptImage.size / 1048576).toFixed(1) + ' MB'}
                                    </span>
                                    <button
                                      type="button"
                                      className="mze-receipt-preview__remove"
                                      onClick={() => this.removeFile(entry.id)}
                                    >
                                      <X size={12} /> {i18n.t('remove')}
                                    </button>
                                  </div>

                                  <div
                                    className={`mze-receipt-preview__body ${entry.receiptImage.type.startsWith('image/') ? 'mze-receipt-preview__body--image' : ''}`}
                                  >
                                    {entry.receiptImage.type.startsWith('image/') && (
                                      <img
                                        src={entry.previewUrl}
                                        alt="Receipt preview"
                                        className="mze-receipt-preview__img"
                                      />
                                    )}
                                    {entry.receiptImage.type === 'application/pdf' && (
                                      <div className="mze-receipt-preview__pdf">
                                        <FileText
                                          size={32}
                                          className="mze-receipt-preview__pdf-icon"
                                        />
                                        <div>
                                          <p className="mze-receipt-preview__pdf-name">
                                            {entry.receiptImage.name}
                                          </p>
                                          <span className="mze-receipt-preview__pdf-meta">
                                            PDF document ·{' '}
                                            {(entry.receiptImage.size / 1048576).toFixed(1)} MB
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  <div className="mze-receipt-preview__actions">
                                    <button
                                      type="button"
                                      className="mze-receipt-preview__view-btn"
                                      onClick={() => window.open(entry.previewUrl, '_blank')}
                                    >
                                      <FileText size={13} /> {i18n.t('view_full_size')}
                                    </button>
                                    <button
                                      type="button"
                                      className="mze-receipt-preview__change-btn"
                                      onClick={() =>
                                        document
                                          .getElementById(`receipt-upload-${entry.id}`)
                                          .click()
                                      }
                                    >
                                      <Upload size={13} /> {i18n.t('change_file')}
                                    </button>
                                  </div>
                                </div>
                              )}

                              {this.renderEntryError(entry.id, 'receiptImage')}
                              {entry.receiptImage &&
                                (() => {
                                  const scanState = this.state.entryScanStates[entry.id] || {};
                                  return (
                                    <>
                                      <div className="mze-scan-row">
                                        <button
                                          type="button"
                                          className={`ai-scan-btn ${scanState.isScanning ? 'ai-scan-btn--scanning' : ''} ${scanState.scanSuccess ? 'ai-scan-btn--success' : ''}`}
                                          onClick={() => this.handleReceiptScan(entry.id)}
                                          disabled={scanState.isScanning || scanState.scanSuccess}
                                        >
                                          {scanState.isScanning ? (
                                            <>
                                              <Loader2 size={16} className="spin" />
                                              <span>{i18n.t('scanning_receipt')}</span>
                                              <span className="ai-scan-btn__dots">
                                                <span />
                                                <span />
                                                <span />
                                              </span>
                                            </>
                                          ) : scanState.scanSuccess ? (
                                            <>
                                              <Check size={16} />
                                              <span>{i18n.t('fields_filled')}</span>
                                            </>
                                          ) : (
                                            <>
                                              <span className="ai-scan-btn__glow" />
                                              <ScanLine size={16} />
                                              <span>{i18n.t('auto_fill_from_receipt')}</span>
                                              <span className="ai-scan-btn__badge">
                                                {i18n.t('ai')}
                                              </span>
                                            </>
                                          )}
                                        </button>
                                      </div>

                                      {scanState.scanError && (
                                        <div className="mze-upload-error" style={{ marginTop: 6 }}>
                                          <AlertCircle size={13} />
                                          {scanState.scanError}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                            </div>

                            <div className="form-group">
                              <label>{i18n.t('transfer_date')}</label>
                              <input
                                type="date"
                                max={today}
                                value={entry.transferDate || ''}
                                onChange={(e) =>
                                  this.updatePaymentEntry(entry.id, 'transferDate', e.target.value)
                                }
                                className={`form-input${this.state.fieldErrors?.[entry.id]?.receipt ? ' input-error' : ''}`}
                              />
                              {this.renderEntryError(entry.id, 'transferDate')}
                            </div>
                            <div className="form-row-flex">
                              <div className="form-group">
                                <label>{i18n.t('receipt_amount')} *</label>
                                <input
                                  type="number"
                                  value={entry.amount}
                                  onWheel={(e) => e.target.blur()}
                                  min="0"
                                  step="0.01"
                                  onChange={(e) =>
                                    this.updatePaymentEntry(entry.id, 'amount', e.target.value)
                                  }
                                  className={`form-input${this.state.fieldErrors?.[entry.id]?.receipt ? ' input-error' : ''}`}
                                  required
                                />
                                {this.renderEntryError(entry.id, 'amount')}
                              </div>
                              <div className="form-group">
                                <label>{i18n.t('receipt_number')} *</label>
                                <input
                                  type="text"
                                  value={entry.receipt}
                                  onChange={(e) =>
                                    this.updatePaymentEntry(entry.id, 'receipt', e.target.value)
                                  }
                                  className={`form-input${this.state.fieldErrors?.[entry.id]?.receipt ? ' input-error' : ''}`}
                                  required
                                />
                                {this.renderEntryError(entry.id, 'receipt')}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Online Payment */}
                <div className={`payment-option {paymentMethod === 'online' ? 'selected' : ''}`}>
                  <div
                    className="option-header"
                    onClick={() => this.setState({ paymentMethod: 'online' })}
                  >
                    <CreditCard className="option-icon" />
                    <span> {i18n.t('online_payment')}</span>
                    {!this.canPayOnline() && (
                      <span className="restriction"> {i18n.t('only_amounts_below')}</span>
                    )}
                    <button
                      className="policy-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.togglePolicy('online');
                      }}
                    >
                      <Info className="icon" />
                    </button>
                  </div>

                  {showPolicies.online && (
                    <div className="policy-section">
                      <h4>{i18n.t('online_deposit_and_refund_policy')}</h4>
                      <ul>
                        <li>{i18n.t('instant_payment_processing')}</li>
                        <li>{i18n.t('refunds_processed_within_days')}</li>
                        <li>{i18n.t('secure_ssl_encryption')}</li>
                        <li>{i18n.t('commission_fees_apply')}</li>
                      </ul>
                    </div>
                  )}

                  {/* {paymentMethod === 'online' && this.canPayOnline() && (
                    <div className="payment-form">
                      <div className="form-group">
                        <label>Card Type</label>
                        <select
                          value={cardType}
                          onChange={(e) => this.setState({ cardType: e.target.value })}
                          className="form-select"
                          disabled
                        >
                          <option value="">Select Card Type</option>
                          <option value="debit">Debit Card (1.5% commission)</option>
                          <option value="credit">Credit Card (2% commission)</option>
                        </select>
                      </div>
                      {cardType && (
                        <div className="commission-info">
                          <span>Commission: {this.getCommission().toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {paymentMethod === 'online' && !this.canPayOnline() && (
                    <div className="restriction-message">
                      <AlertCircle className="icon" />
                      <span>Online payment only available for amounts below 5000</span>
                    </div>
                  )} */}
                </div>

                {/* Wallet Payment */}
                <div className={`payment-option {paymentMethod === 'wallet' ? 'selected' : ''}`}>
                  <div
                    className="option-header"
                    onClick={() => this.setState({ paymentMethod: 'wallet' })}
                  >
                    <Wallet className="option-icon" />
                    <span>{i18n.t('wallet_payment')}</span>
                    <span className="wallet-balance">
                      {i18n.t('balance')}: {walletBalance}
                    </span>
                  </div>

                  {paymentMethod === 'wallet' && (
                    <div className="wallet-info">
                      {this.canPayWithWallet() ? (
                        <div className="wallet-sufficient">
                          <CheckCircle className="icon" />
                          <span>{i18n.t('sufficient_wallet_balance')}</span>
                        </div>
                      ) : (
                        <div className="wallet-insufficient">
                          <AlertCircle className="icon" />
                          <span>
                            {i18n.t('insufficient_wallet_balance')} (Need:{' '}
                            {totalPaymentAmount.toFixed(2)})
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {paymentMethod && (
                <div className="payment-summary">
                  <div className="summary-section">
                    <h4>{i18n.t('final_payment_summary')}</h4>
                    <div className="summary-row">
                      <span>{i18n.t('pending_balance')}</span>
                      <span>{this.getPendingBalance().toFixed(2)}</span>
                    </div>
                    <div className="summary-row">
                      <span> {i18n.t('total_payment_amount')}</span>
                      <span>{totalPaymentAmount.toFixed(2)}</span>
                    </div>
                    {paymentMethod === 'online' && cardType && (
                      <div className="summary-row">
                        <span>
                          {i18n.t('commission')} ({cardType === 'debit' ? '1.5%' : '2%'}):
                        </span>
                        <span>{this.getCommission().toFixed(2)}</span>
                      </div>
                    )}
                    <div className="summary-row total">
                      <span>{i18n.t('subtotal')}</span>
                      <span>{this.getSubtotal().toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="action-buttons-new">
                    <button className="modal-btn btn-cancel cancel-btn" onClick={this.handleCancel}>
                      {i18n.t('cancel')}
                    </button>
                    <button
                      className="modal-btn btn-gradient pay-btn"
                      onClick={this.handlePayment}
                      disabled={
                        (paymentMethod === 'wallet' && !this.canPayWithWallet()) ||
                        (paymentMethod === 'online' && !this.canPayOnline()) ||
                        totalPaymentAmount === 0
                      }
                    >
                      {this.state.submitting ? (
                        <>
                          <span className="spinner"></span>
                          {i18n.t('processing_payment')}
                        </>
                      ) : (
                        <>
                          {i18n.t('pay')} {this.getSubtotal().toFixed(2)} {i18n.t('now')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </CustomModal>
    );
  }
}
